import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import hasFeature from 'modules/core/lib/hasFeature'

export const EMPLOYEE_STATUS = {
  active: 'employee_active',
  terminated: 'employee_terminated',
} as const

/**
 Use this type instead of MultiFilterForm once the new multi filters are fully implemented
 */
export const LOGIC_OPERATOR = {
  AND: 'and',
  OR: 'or',
} as const

type LogicOperator = (typeof LOGIC_OPERATOR)[keyof typeof LOGIC_OPERATOR]

export const SHIFT_CONFLICTS = 'shift_conflicts'
export const SHIFT_WITHOUT_CONFLICTS = 'shift_without_conflicts'

const SHIFT_MANAGEMENT_FILTERS = {
  employeeLocation: 'location',
  employeeTeam: 'team',
} as const

export interface CustomFilter {
  field: string
  condition: string
  value: string
}

export interface FilterState {
  customFilters: CustomFilter[]
  statusId: string | null
  employeeIds: string[] | null
  logicOperator: string | null
  shiftIds: number[] | null
  withoutShiftIds: number[] | null
  terminated: boolean
  locationIds: number[]
  teamIds: number[] | null
  legalEntityIds: number[] | null
}

export type FilterKeys =
  | 'teams'
  | 'employees'
  | 'status'
  | 'employee_status'
  | 'legal_entities'
  | 'shift_work_areas'

export type FiltersDispatch = (arg0: FiltersAction) => void

export type FiltersAction =
  | {
      type: 'CLEAR_FILTERS'
    }
  | {
      type: 'FILTER_CUSTOM'
      id: string | null
      employeeIds: string[] | null
      shiftIds: number[] | null
      withoutShiftIds: number[] | null
      customFilters: CustomFilter[]
      logicOperator: MultiFilterForm
      terminated: boolean
      teamIds: number[] | null
      legalEntityIds: number[] | null
    }
  | {
      type: 'FILTER_EMPLOYEE_IDS'
      employeeIds: string[]
    }
  | {
      type: 'RESET_SHIFT_IDS'
    }
  | {
      type: 'SET_LOCATIONS'
      locationIds: number[]
    }

export function initialFiltersState(): FilterState {
  return {
    customFilters: [],
    statusId: null,
    employeeIds: null,
    shiftIds: null,
    withoutShiftIds: null,
    logicOperator: null,
    terminated: false,
    locationIds: [],
    teamIds: null,
    legalEntityIds: null,
  }
}

function isEmptyState(filter: FilterState): boolean {
  return (
    !filter.customFilters.length &&
    !filter.statusId &&
    !filter.employeeIds &&
    !filter.shiftIds &&
    !filter.withoutShiftIds &&
    !filter.logicOperator &&
    !filter.terminated &&
    !filter.locationIds &&
    !filter.teamIds &&
    !filter.legalEntityIds
  )
}

export default function reducer(state: FilterState, action: FiltersAction): FilterState {
  switch (action.type) {
    case 'FILTER_CUSTOM':
      return {
        ...state,
        customFilters: action.customFilters,
        statusId: action.id,
        employeeIds: action.employeeIds,
        shiftIds: action.shiftIds,
        withoutShiftIds: action.withoutShiftIds,
        logicOperator: action.logicOperator.get('logic_operator').value,
        terminated: action.terminated,
        teamIds: action.teamIds,
        legalEntityIds: action.legalEntityIds,
      }

    case 'FILTER_EMPLOYEE_IDS':
      return {
        ...state,
        customFilters: [],
        statusId: null,
        employeeIds: action.employeeIds,
        shiftIds: null,
        withoutShiftIds: null,
        logicOperator: null,
        terminated: false,
        teamIds: null,
        legalEntityIds: null,
      }

    case 'CLEAR_FILTERS':
      // When it's GraphQL we should reset the state to refetch the query.
      if (isEmptyState(state) && !hasFeature('dev_shift_management_graphql_migration')) return state

      // Location filtering is managed by the LocationSelector component which handles filter state management, so we preserve existing locationIds
      return {
        ...initialFiltersState(),
        locationIds: state.locationIds,
      }

    case 'RESET_SHIFT_IDS':
      return {
        ...state,
        shiftIds: null,
        withoutShiftIds: null,
      }

    case 'SET_LOCATIONS':
      return {
        ...state,
        locationIds: action.locationIds,
      }

    default:
      return state
  }
}
